<template>
  <div class='diverging-stacked-bar-chart'>
    <div class='diverging-stacked-bar-chart__labels'>
      <div  class='diverging-stacked-bar-chart__labels__label'>{{ $t('complience.label.fail') }}</div>
      <div  class='diverging-stacked-bar-chart__labels__label'>{{ $t('complience.label.pass') }}</div>
    </div>
    <svg class='diverging-stacked-bar-chart-content-svg__content' :width='width' :height='dimensions.height'
         xmlns='http://www.w3.org/2000/svg'
         xmlns:xlink='http://www.w3.org/1999/xlink'
    />
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: 'DivergingStackedBarChart',
  props: {
    data: {
      type: Array,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    width: {
      default: 300,
      type: Number,
    },
    margin: {
      type: Object,
      required: false,
      default: () => ({
        top: 9,
        bottom: 0,
        left: 140,
        right: 0,
        barHeight: 30,
        barOffsetBottom: 39.5,
      }),
    },
  },
  data() {
    return {
    };
  },
  mounted() {
    this.buildChart();
  },
  computed: {
    dimensions() {
      const calculatedHeight = Math.ceil((this.data.length + 0.1) * this.margin.barHeight) + Math.ceil((this.data.length + 0.1) * this.margin.barOffsetBottom) + this.margin.top + this.margin.bottom;
      return {
        height: calculatedHeight,
        width: this.width,
        boundedWidth: this.width - this.margin.left - this.margin.right,
        boundedHeight: calculatedHeight - this.margin.top - this.margin.bottom,
        ...this.margin,
      };
    },
  },
  methods: {
    buildChart() {
      const svg = d3.select('.diverging-stacked-bar-chart-content-svg__content');
      const xScale = d3.scaleLinear()
        .domain(d3.extent(this.data, d => (d.data.fail + d.data.pass)))
        .range([this.dimensions.left, this.dimensions.width - this.dimensions.right]);
      const yScale = d3.scaleBand()
        .domain(d3.range(this.data.length))
        .rangeRound([this.dimensions.top, this.dimensions.height - this.dimensions.bottom])
        .padding(0.1);
      // Create bars
      // Failed tests
      svg.append('g')
        .selectAll('rect')
        .data(this.data)
        .join('rect')
        .attr('fill', '#DF399F')
        .attr('x', d => xScale(Math.min(-1 * d.data.fail, 0)))
        .attr('y', (d, i) => yScale(i) + i)
        .attr('testY', (d, i) => i % 2)
        .attr('width', d => Math.abs(xScale(d.data.fail)) - xScale(0))
        .attr('height', yScale.bandwidth() - this.dimensions.barOffsetBottom)
        .style('fill-opacity', 0.8);
      // Passed text
      svg.append('g')
        .selectAll('rect')
        .data(this.data)
        .join('rect')
        .attr('fill', '#00D0C4')
        .attr('x', d => xScale(Math.min(d.data.pass, 0)))
        .attr('y', (d, i) => yScale(i) + i)
        .attr('width', d => Math.abs(xScale(d.data.pass)) - xScale(0))
        .attr('height', yScale.bandwidth() - this.dimensions.barOffsetBottom)
        .style('fill-opacity', 0.8);
      // Add zero line
      svg.append('line')
        .style('stroke', '#1a1e22')
        .style('stroke-width', 0.5)
        .attr('x1', xScale(0))
        .attr('y1', yScale(0))
        .attr('x2', xScale(0))
        .attr('y2', yScale(this.data.length - 1) + yScale.bandwidth() - this.dimensions.barOffsetBottom + 30);
      // Add labels at end of bar text labels
      svg.append('g')
        .attr('class', 'bar-label-group')
        .selectAll('text')
        .data(this.data)
        .join(enter => {
          const g = enter.append('g');
          g.append('text')
            .text(d => d.data.pass)
            .attr('text-anchor', 'start')
            .attr('x', d => xScale(Math.min(d.data.pass, 0)) + Math.abs(xScale(d.data.pass)) - xScale(0) + 5)
            .attr('y', (d, i) => yScale(i) + 15 + i);
          g.append('text')
            .text(d => d.data.fail)
            .attr('text-anchor', 'end')
            .attr('x', d => xScale(Math.min(-1 * d.data.fail, 0)) - 5)
            .attr('y', (d, i) => yScale(i) + 15 + i);
          return g;
        });
    },
  },
};
</script>
<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.diverging-stacked-bar-chart {
  display: flex;
  flex-direction: column;

  &__labels {
    display: flex;
    flex-direction: row;
    margin-top: rem(-15);
    justify-content: center;

    &__label {
      margin-right: rem(20);
      font-family: $font-family-regular;
      font-size: $font-size-sm;
      fill: var(--text-primary);
      text-transform: lowercase;
      font-style: italic;
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.diverging-stacked-bar-chart-content-svg {
  &__content {
    margin-top: rem(-5);

    .bar-label-group {
      font-family: $font-family-regular;
      font-size: $font-size-xs;
      fill: var(--text-primary);
    }
  }
}
</style>
